import { Domain } from 'src/apollo/sites'

import { OptionalToastLocalSiteData, SiteData } from 'shared/js/types'
import urlJoin from 'url-join'
import { TOASTTAB_BOO_PATH } from 'src/public/js/siteUtilities'
import { resources } from 'config'

export const GENERIC_SITE_SUFFIX = '.toast.site'
export const LEGACY_GENERIC_SITE_SUFFIX = 'toast.ventures'

export const isToastVenturesDomain = (d: Domain) =>
  d.domain.endsWith(LEGACY_GENERIC_SITE_SUFFIX)
export const isGenericSite = (d: Domain) =>
  d.domain.endsWith(GENERIC_SITE_SUFFIX)
export const isWwwSubDomain = (d: Domain) => d.domain.startsWith('www.')
export const isActiveDomain = (d: Domain) => !d.pendingVerification
export const isHomePageDomain = (d: Domain) =>
  d.pageRoute === null || d.pageRoute === '/'

export const getDomainPriority = (d: Domain) => {
  if (isToastVenturesDomain(d)) return 3
  if (isGenericSite(d)) return 2
  if (isWwwSubDomain(d)) return 1
  return 0
}
export const sortDomainsByPriority = (a: Domain, b: Domain) => {
  return getDomainPriority(a) - getDomainPriority(b)
}

export const extractActiveHomePageDomains = (
  optionalSiteData: OptionalToastLocalSiteData
) => {
  if (!optionalSiteData) return
  const { config, domainsV2: domains, additionalPages } = optionalSiteData
  const hasAdditionalHomePage = additionalPages?.some((p) => p.route === '/')
  if (
    (config?.homePageConfig?.optedOut === true && !hasAdditionalHomePage) ||
    !domains
  ) {
    return
  }

  return domains
    ?.filter(isActiveDomain)
    ?.filter(isHomePageDomain)
    ?.sort(sortDomainsByPriority)
    ?.map((d) => d?.domain)
}

// Custom domains only mount /order/* routes, not /online/*.
// When building a canonical URL pointing to a custom domain, translate the path.
const getCanonicalPath = (pathname: string): string =>
  pathname.replace(/^\/online\//, '/order/')

// Build the canonical URL for a page. If the restaurant has a custom domain,
// point there (translating /online/* to /order/* since custom domains don't
// mount /online/* routes). Without a custom domain the current URL is canonical
// and the /online/* path is preserved as-is.
export const buildCanonicalUrl = (
  currentUrl: string,
  pathname: string,
  primaryCustomDomain: string | null | undefined
): string => {
  if (!primaryCustomDomain) return currentUrl
  return `https://${primaryCustomDomain}${getCanonicalPath(pathname)}`
}

type DomainRouteInfo = Pick<
  Domain,
  'domain' | 'pageRoute' | 'pendingVerification'
>

// A verified subdomain can be mapped to a page route (e.g. orders.rx.com -> "/order"); the
// router then renders that page's component at the domain root. Returns the page route that
// `host` is mapped to, or null when the host isn't a verified domain or has no page route.
export const getPageRouteForHost = (
  host: string | undefined,
  domains: DomainRouteInfo[] | undefined | null
): string | null => {
  if (!host || !domains) return null
  return (
    domains.find((d) => !d.pendingVerification && d.domain === host)
      ?.pageRoute ?? null
  )
}

export const getPrimaryCustomDomain = (siteData?: SiteData): string | null => {
  if (!siteData?.domainsV2) return null

  const customDomains = siteData.domainsV2
    .filter(
      (domain) =>
        domain.userCreated === true &&
        !domain.pendingVerification &&
        !domain.domain.endsWith(GENERIC_SITE_SUFFIX) &&
        !domain.domain.endsWith(LEGACY_GENERIC_SITE_SUFFIX)
    )
    .sort(sortDomainsByPriority)

  return customDomains.length > 0 ? customDomains[0]?.domain || null : null
}

export const getToasttabCheckoutUrl = (shortUrl: string, cartGuid: string) => {
  const params = new URLSearchParams({ cartGuid })
  const url = urlJoin(
    `https://${resources.toastOrderHost}`,
    TOASTTAB_BOO_PATH,
    shortUrl,
    '/checkout',
    '?' + params.toString()
  )
  return url
}
